import React, { useEffect, useState } from "react";
import { Download, Trash2, FileDown } from "lucide-react";
import { Tooltip, Popconfirm, Input } from "antd";

import {
  useAllOrders,
  useCancelOrder,
  useDownloadLabel,
  useDownloadManifest,
  useTrackOrder,
} from "../../../../../api/orders/orders";
import { useLoader } from "../../../../../utils/LoaderContext";

const ORDERS_PER_PAGE = 20;

const Manifest = ({ status = "shipped" }) => {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrders, setSelectedOrders] = useState([]);

  const { setLoading } = useLoader();

  // Debounce search
  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 400);
    return () => clearTimeout(delay);
  }, [searchText]);

  // Fetch orders with search parameter passed to API
  const { data, isLoading } = useAllOrders(
    { 
      currentPage, 
      ORDERS_PER_PAGE, 
      status,
      search: debouncedSearchText // Pass search to API
    },
    {
      enabled: true,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: cancelOrder } = useCancelOrder();
  const { mutate: downloadLabel, isLoading: downloading } = useDownloadLabel();
  const { mutate: downloadManifest, isLoading: downloadingManifest } =
    useDownloadManifest();
  const { mutate: trackOrder } = useTrackOrder();

  // Get orders and pagination info from API
  const orders = data?.orders || [];
  const totalPages = data?.totalPages || 1;
  const totalOrders = data?.totalOrders || 0;

  // ✅ Select All (only current page)
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(
        orders.map((order) => ({
          awbNumber: order.awbNumber,
          shipment_id: order.shipment_id,
          full: order,
        }))
      );
    } else {
      setSelectedOrders([]);
    }
  };

  // ✅ Select Single
  const handleSelectOne = (order) => {
    setSelectedOrders((prev) =>
      prev.some((o) => o.shipment_id === order.shipment_id)
        ? prev.filter((o) => o.shipment_id !== order.shipment_id)
        : [
            ...prev,
            {
              awbNumber: order.awbNumber,
              shipment_id: order.shipment_id,
              full: order,
            },
          ]
    );
  };

  // 🚀 Bulk download with SHIPMENT IDs
  const handleDownloadAll = () => {
    const shipmentIds = selectedOrders.map((o) => o.shipment_id);
    downloadLabel(shipmentIds);
  };

  // Clear selections when page changes
  useEffect(() => {
    setSelectedOrders([]);
  }, [currentPage]);

  return (
    <div className="w-full mt-6">
      {/* Search + Download All */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
        />
        <div className="flex items-center gap-4">
          {totalOrders > 0 && (
            <div className="text-sm text-gray-600">
              Total Orders: <strong>{totalOrders}</strong>
            </div>
          )}
          {selectedOrders.length > 0 && (
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50"
              onClick={handleDownloadAll}
              disabled={downloading}
            >
              Download Selected ({selectedOrders.length})
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
        <table className="min-w-[1500px] table-auto">
          <thead className="bg-gray-50 text-xs font-semibold text-gray-700 uppercase">
            <tr>
              <th className="p-3">
                <input
                  type="checkbox"
                  checked={
                    orders.length > 0 &&
                    orders.every((order) =>
                      selectedOrders.some(
                        (o) => o.shipment_id === order.shipment_id
                      )
                    )
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order ID / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Address</th>
              <th className="p-3">Courier / Tracking</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>

          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan="9">
                  <div className="flex justify-center items-center py-10 text-gray-500 space-x-2">
                    <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                    <span>Loading orders...</span>
                  </div>
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-6 text-gray-400">
                  {debouncedSearchText
                    ? "No orders found matching your search."
                    : "No orders found."}
                </td>
              </tr>
            ) : (
              orders.map((order, index) => (
                <tr key={order._id || index} className="hover:bg-gray-50">
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedOrders.some(
                        (o) => o.shipment_id === order.shipment_id
                      )}
                      onChange={() => handleSelectOne(order)}
                    />
                  </td>

                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>

                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.OrderNumber}
                    </div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                        order.shippingStatusText === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : order.shippingStatusText === "in_transit" ||
                            order.shippingStatusText === "In Transit"
                          ? "bg-blue-100 text-blue-600"
                          : order.shippingStatusText === "shipped" ||
                            order.shippingStatusText === "Shipped"
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {order.shippingStatusText}
                    </div>
                  </td>

                  <td className="p-4">
                    ₹{order.InvoiceAmount}
                    <div className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded mt-1 inline-block">
                      {order.PaymentType}
                    </div>
                  </td>

                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name || prod.Name}
                      </p>
                    ))}
                  </td>

                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.Contact}</p>
                  </td>

                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.City}</p>
                    <p>{order.BillingAddress?.State}</p>
                    <p>{order.BillingAddress?.Pincode}</p>
                  </td>

                  <td className="p-4">
                    <p>{order?.courier}</p>
                    {order.awbNumber ? (
                      <button
                        onClick={() => trackOrder(order.awbNumber)}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors duration-200"
                      >
                        Track Order
                      </button>
                    ) : (
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded">
                        AWB Not Available
                      </span>
                    )}
                  </td>

                  <td className="p-4 space-x-2">
                    {order.shipment_id && (
                      <Tooltip title="Download Label">
                        <button
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm disabled:opacity-50"
                          onClick={() => downloadLabel([order.shipment_id])}
                          disabled={downloading}
                        >
                          <Download size={16} />
                        </button>
                      </Tooltip>
                    )}

                    {order.shipment_id && (
                      <button
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded text-sm disabled:opacity-50"
                        onClick={() => downloadManifest([order.shipment_id])}
                        disabled={downloadingManifest}
                        title="Download Manifest"
                      >
                        <FileDown size={16} />
                      </button>
                    )}

                    <Tooltip title="Cancel Order">
                      <Popconfirm
                        title="Are you sure you want to cancel this order?"
                        onConfirm={() => cancelOrder(order.shiprocket_order_id)}
                        okText="Yes"
                        cancelText="No"
                        placement="topRight"
                      >
                        <button className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded">
                          <Trash2 size={16} />
                        </button>
                      </Popconfirm>
                    </Tooltip>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalOrders > 0 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            {" "}({totalOrders} total orders)
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || isLoading}
              className={`px-4 py-2 rounded border ${
                currentPage === 1 || isLoading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || isLoading}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages || isLoading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Manifest;